import type { AxiosResponse, AxiosError } from 'axios';
import { logAnalyticsEvent, AnalyticsEventMetadata } from './analytics';

/**
 * Pattern definition for matching an API response to a semantic event.
 * Each app provides its own list of patterns covering the URLs its
 * services expose.
 */
export interface ApiEventPattern {
  /** HTTP method (uppercase). */
  method: string;
  /** Predicate against the path (query string already stripped). */
  match: (path: string) => boolean;
  /** Statsig event name to log. */
  event: string;
  /**
   * HTTP status this pattern applies to. Omitted (the default) means "a successful response" —
   * such patterns are checked by `onResponse` only. A pattern that names a specific non-2xx status
   * (e.g. `409`) is checked by `onError` only, against `error.response.status` — this is how an
   * expected, semantic failure (a publish blocked by a conformance gate) gets its own event
   * instead of folding into the generic `api_failure` every other error still logs.
   */
  status?: number;
  /**
   * Extract metadata from the request body, the response data, and the matched request path
   * (query stripped). The path is the only source for route params: a call like
   * `POST /compositions/:systemName/versions/:v/publish` carries no request body at all, so
   * `systemName` is readable only from the path.
   */
  extract?: (body: unknown, data: unknown, path: string) => AnalyticsEventMetadata;
  /** Optional value (often resource id) — Statsig second arg. Receives the same three inputs. */
  value?: (body: unknown, data: unknown, path: string) => string | number | undefined;
}

const stripQuery = (url: string): string => url.split('?')[0];

/** Helpers for building patterns. Exported for app config files. */
export const pathExact =
  (path: string) =>
  (url: string): boolean =>
    url === path;

export const pathStartsWith =
  (prefix: string) =>
  (url: string): boolean =>
    url.startsWith(prefix);

export const pathMatches =
  (re: RegExp) =>
  (url: string): boolean =>
    re.test(url);

const parseRequestBody = (raw: unknown): unknown => {
  if (raw == null) return undefined;
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

/**
 * Factory: returns onResponse / onError handlers for ApiClientsProvider.
 *
 * The success handler matches the response against status-less patterns and logs the first
 * matching event (none if no match).
 *
 * The error handler first checks patterns that declare a `status` matching the response's actual
 * status — a match logs that semantic event INSTEAD of the generic failure event, because a
 * pattern-matched error status (e.g. a publish blocked by a conformance gate returning 409) is
 * expected, gated behavior, not a bug; folding it into `api_failure` would inflate failure-rate
 * metrics for something the product deliberately did. Any error that matches no status-scoped
 * pattern still logs `api_failure` so genuine failure rates stay visible.
 */
export function createApiResponseLogger(patterns: ApiEventPattern[]): {
  onResponse: (response: AxiosResponse) => void;
  onError: (error: AxiosError) => void;
} {
  return {
    onResponse: (response: AxiosResponse) => {
      const method = (response.config.method ?? '').toUpperCase();
      const path = stripQuery(response.config.url ?? '');
      for (const pattern of patterns) {
        if (pattern.status !== undefined) continue;
        if (pattern.method !== method) continue;
        if (!pattern.match(path)) continue;
        const body = parseRequestBody(response.config.data);
        logAnalyticsEvent(
          pattern.event,
          pattern.value?.(body, response.data, path),
          pattern.extract?.(body, response.data, path)
        );
        return;
      }
    },

    onError: (error: AxiosError) => {
      const method = (error.config?.method ?? '').toUpperCase();
      const path = stripQuery(error.config?.url ?? '');
      const status = error.response?.status;

      for (const pattern of patterns) {
        if (pattern.status === undefined || pattern.status !== status) continue;
        if (pattern.method !== method) continue;
        if (!pattern.match(path)) continue;
        const body = parseRequestBody(error.config?.data);
        logAnalyticsEvent(
          pattern.event,
          pattern.value?.(body, error.response?.data, path),
          pattern.extract?.(body, error.response?.data, path)
        );
        return;
      }

      logAnalyticsEvent('api_failure', status, {
        method,
        url: path,
        status: status ?? null,
        message: error.message,
      });
    },
  };
}
